// PreloadProvider.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLoader } from '@react-three/fiber';
import { TextureLoader } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { preloadAssets } from './preloadAssets';

const PreloadContext = createContext();

// Create a cache to store loaded assets
const assetCache = new Map();

// Create a progress context
const ProgressContext = createContext({ 
  progress: 0,
  setProgress: () => {} 
});

export function ProgressProvider({ children }) {
  const [progress, setProgress] = useState(0);
  
  const value = React.useMemo(() => ({
    progress,
    setProgress
  }), [progress]);

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
}

export function useLoadingProgress() {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useLoadingProgress must be used within a ProgressProvider');
  }
  return context;
}

export function PreloadProvider({ children, room, onLoadComplete }) {
  const { progress, setProgress } = useLoadingProgress();
  const [isLoading, setIsLoading] = useState(true);
  const [assets, setAssets] = useState({
    models: [],
    environments: [],
    swatches: [],
    textures: []
  });

  // Add progress callback
  const handleProgress = (p) => {
    setProgress(p * 100);
  };

  useEffect(() => {
    if (!room) {
      setIsLoading(false);
      setProgress(100);
      onLoadComplete?.();
      return;
    }

    // Reset loading state when room changes
    setIsLoading(true);
    setProgress(0);

    if (assetCache.has(room.name)) {
      setAssets(assetCache.get(room.name));
      setIsLoading(false);
      setProgress(100);
      onLoadComplete?.();
      return;
    }

    let isMounted = true;

    // Load room assets
    const loadAssets = async () => {
      try {
        const result = await preloadAssets(room, handleProgress);
        if (!isMounted) return;

        if (!result || !Array.isArray(result)) {
          setIsLoading(false);
          setProgress(100);
          onLoadComplete?.();
          return;
        }

        const [models, environments, swatches, textures] = result;
        const loadedAssets = {
          models: models || [],
          environments: environments || [],
          swatches: swatches || [],
          textures: textures || []
        };

        assetCache.set(room.name, loadedAssets);
        
        if (isMounted) {
          setAssets(loadedAssets);
          setIsLoading(false);
          setProgress(100);
          onLoadComplete?.();
        }
      } catch (error) {
        if (isMounted) {
          setIsLoading(false);
          setProgress(100);
          onLoadComplete?.();
        }
      }
    };

    loadAssets();

    // Cleanup function
    return () => { isMounted = false; };
  }, [room]);

  return (
    <PreloadContext.Provider value={{ ...assets, isLoading }}>
      {children}
    </PreloadContext.Provider>
  );
}

export function usePreload() {
  const context = useContext(PreloadContext);
  if (!context) {
    throw new Error('usePreload must be used within a PreloadProvider');
  }
  return context;
}
